import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { 
  Send, MessageCircle, Copy, ExternalLink, Loader2, 
  Sparkles, RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
  CheckCircle, Lightbulb, HelpCircle, ArrowLeft, User, Search,
  Target, Brain, TrendingUp, Home, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

// SAIRA capabilities list for About section
const SAIRA_CAPABILITIES = [
  "Write follow-up messages & WhatsApp drafts",
  "Objection handling scripts (pricing, location, competition)",
  "Project comparison & recommendation based on customer needs",
  "Next-best action for your pipeline (what to do today)",
  "Negotiation help + closing lines",
  "🆕 CRM Copilot: \"Which customers need follow-up today?\"",
  "🆕 Market Intel: \"What's the latest news in Bangalore?\""
];

// Empty state quick suggestion chips
const EMPTY_STATE_CHIPS = [
  { label: "Give me a follow-up message", icon: "📱" },
  { label: "Handle price objection", icon: "💰" },
  { label: "Help me close this deal", icon: "🏆" },
  { label: "Which customers need follow-up today?", icon: "📞" },
  { label: "What's the market news?", icon: "📰" }
];

// Objection Battle Card Component
const ObjectionCard = ({ item, index }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleCopyScript = () => {
    const script = `Customer: "${item.objection}"\n\nResponse: ${item.answer}`;
    navigator.clipboard.writeText(script);
    setCopied(true);
    toast.success('Script copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse answer for bullet points
  const formatAnswer = (answer) => {
    if (!answer) return null;
    
    // Check if answer contains bullet points (•, -, *)
    const hasBullets = /[•\-\*]\s/.test(answer);
    
    if (hasBullets) {
      // Split by bullet patterns and filter empty
      const parts = answer.split(/(?=[•\-\*]\s)/);
      const intro = parts[0]?.trim();
      const bullets = parts.slice(1).map(p => p.replace(/^[•\-\*]\s*/, '').trim()).filter(Boolean);
      
      return (
        <>
          {intro && <p className="text-sm text-emerald-800 mb-2">{intro}</p>}
          <ul className="space-y-1">
            {bullets.map((bullet, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                <span className="text-emerald-500 mt-0.5">•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </>
      );
    }
    
    return <p className="text-sm text-emerald-800">{answer}</p>;
  };

  return (
    <div className="bg-white rounded-xl border border-amber-200 overflow-hidden shadow-sm">
      {/* Header - Collapsible Toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2.5 flex items-center justify-between bg-amber-100/50 hover:bg-amber-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-left">
          <span className="text-red-500 text-sm">❌</span>
          <span className="text-xs font-semibold text-amber-800 uppercase">Customer</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-amber-600">#{index + 1}</span>
          {isExpanded ? <ChevronUp size={14} className="text-amber-600" /> : <ChevronDown size={14} className="text-amber-600" />}
        </div>
      </button>
      
      {/* Objection Text */}
      <div className="px-3 py-2 border-b border-amber-100 bg-red-50/30">
        <p className="text-sm text-amber-900 font-medium italic">"{item.objection}"</p>
      </div>
      
      {/* Answer Section - Collapsible */}
      {isExpanded && (
        <div className="px-3 py-3 bg-emerald-50/50">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-emerald-500 text-sm">✅</span>
            <span className="text-xs font-semibold text-emerald-700 uppercase">Your Answer</span>
          </div>
          
          {formatAnswer(item.answer)}
          
          {/* Copy Script Button */}
          <button
            onClick={handleCopyScript}
            className="mt-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-emerald-200 rounded-lg text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
          >
            {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
            {copied ? 'Copied!' : 'Copy Script'}
          </button>
        </div>
      )}
    </div>
  );
};

// Updated quick chips for Manager Mode (coaching focus)
const QUICK_CHIPS = [
  { label: 'What next?', icon: '🎯', prompt: 'What should I do next with this lead?' },
  { label: 'Analyze Deal', icon: '🧠', prompt: 'Analyze this deal and tell me where I stand.' },
  { label: 'Price Objection', icon: '💰', prompt: 'Customer says the price is too high. How should I handle this?' },
  { label: 'Draft Message', icon: '📱', prompt: 'Draft a WhatsApp follow-up message for this customer.' },
  { label: 'Closing Strategy', icon: '🏆', prompt: 'What\'s the best strategy to close this deal?' },
];

const SAIRA = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  
  // Get lead context from URL if present
  const leadId = searchParams.get('lead_id');
  const leadName = searchParams.get('lead_name');
  const leadProject = searchParams.get('project');
  
  // Initialize messages from localStorage or empty
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('saira_messages');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingState, setLoadingState] = useState('idle'); // idle | thinking | slow | timeout | error
  const [loadingDuration, setLoadingDuration] = useState(0);
  const [tone, setTone] = useState('formal');
  const [channel, setChannel] = useState('whatsapp');
  const [showSettings, setShowSettings] = useState(false);
  const [selectedLead, setSelectedLead] = useState(leadId ? { id: leadId, name: leadName, project: leadProject } : null);
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedResponse, setExpandedResponse] = useState(null);
  const abortControllerRef = useRef(null);
  const loadingTimerRef = useRef(null);
  const loadingIntervalRef = useRef(null);
  
  // Persist messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('saira_messages', JSON.stringify(messages));
    }
  }, [messages]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load history from backend if lead context changes or on mount
  useEffect(() => {
    const loadHistoryOnMount = async () => {
      // If we have a lead_id from URL, load its specific history
      if (leadId) {
        loadHistory(leadId);
      } else {
        // Otherwise load general history if no cached messages
        const saved = localStorage.getItem('saira_messages');
        if (!saved || JSON.parse(saved).length === 0) {
          loadHistory(null);
        }
      }
    };
    loadHistoryOnMount();
  }, [leadId]);

  const loadHistory = async (lid) => {
    try {
      const res = await api.getSairaHistory(lid, 10);
      const history = res.data.conversations || [];
      if (history.length > 0) {
        // Convert to message format
        const historyMessages = history.reverse().flatMap(conv => [
          { type: 'user', content: conv.user_question, timestamp: conv.created_at },
          { type: 'assistant', content: conv.response, conversationId: conv.conversation_id, timestamp: conv.created_at }
        ]);
        setMessages(historyMessages);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const searchLeads = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      const res = await api.getCustomers({ search: query, limit: 10 });
      setSearchResults(res.data.customers || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSend = async (customPrompt = null) => {
    const question = customPrompt || inputValue.trim();
    if (!question) return;
    
    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: question, timestamp: new Date().toISOString() }]);
    setInputValue('');
    setIsLoading(true);
    setLoadingState('thinking');
    setLoadingDuration(0);
    
    // Start progress tracking
    const startTime = Date.now();
    loadingIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setLoadingDuration(elapsed);
      
      // Update loading state based on duration
      if (elapsed >= 45) {
        setLoadingState('timeout');
      } else if (elapsed >= 15) {
        setLoadingState('slow');
      }
    }, 1000);
    
    // Get the last conversation_id if exists (for multi-turn)
    const lastAssistantMsg = [...messages].reverse().find(m => m.type === 'assistant');
    const conversationId = lastAssistantMsg?.conversationId || null;
    
    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();
    
    try {
      const payload = {
        user_question: question,
        lead_id: selectedLead?.id || null,
        tone,
        channel,
        conversation_id: conversationId,
        context_fields: selectedLead ? {
          lead_name: selectedLead.name,
          project: selectedLead.project
        } : null
      };
      
      const res = await api.sairaCoach(payload, { signal: abortControllerRef.current.signal });
      
      // Clear timers
      clearInterval(loadingIntervalRef.current);
      
      if (res.data.success) {
        setMessages(prev => [...prev, { 
          type: 'assistant', 
          content: res.data, 
          conversationId: res.data.conversation_id,
          timestamp: new Date().toISOString()
        }]);
        setLoadingState('idle');
      } else {
        throw new Error('SAIRA could not process your request');
      }
    } catch (error) {
      // Clear timers
      clearInterval(loadingIntervalRef.current);
      
      // Handle different error types
      if (error.name === 'CanceledError' || error.message === 'canceled') {
        toast.info('Request cancelled');
        setLoadingState('idle');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setLoadingState('error');
        toast.error('Request timed out. SAIRA is taking too long to respond.');
        // Keep the user message so they can retry
      } else if (!navigator.onLine) {
        setLoadingState('error');
        toast.error('No internet connection. Please check your network.');
        setMessages(prev => prev.slice(0, -1));
      } else {
        setLoadingState('error');
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to get response';
        toast.error(errorMsg);
        // Remove user message on error
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };
  
  const handleCancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      clearInterval(loadingIntervalRef.current);
      setIsLoading(false);
      setLoadingState('idle');
      // Remove the pending user message
      setMessages(prev => prev.slice(0, -1));
    }
  };
  
  const handleRetry = () => {
    // Get the last user message and retry
    const lastUserMsg = [...messages].reverse().find(m => m.type === 'user');
    if (lastUserMsg) {
      // Remove the last user message first (handleSend will re-add it)
      setMessages(prev => prev.slice(0, -1));
      setLoadingState('idle');
      handleSend(lastUserMsg.content);
    }
  };

  const handleCopy = async (text, conversationId) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
      if (conversationId) {
        api.trackSairaShare(conversationId, 'copy').catch(() => {});
      }
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleShareWhatsApp = (text, conversationId) => {
    const phone = selectedLead?.phone?.replace(/\D/g, '') || '';
    const encodedMessage = encodeURIComponent(text);
    const whatsAppUrl = phone 
      ? `https://wa.me/91${phone}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsAppUrl, '_blank');
    if (conversationId) {
      api.trackSairaShare(conversationId, 'whatsapp').catch(() => {});
    }
  };

  const handleRegenerate = () => {
    if (messages.length >= 2) {
      const lastUserMsg = [...messages].reverse().find(m => m.type === 'user');
      if (lastUserMsg) {
        // Remove last assistant response
        setMessages(prev => prev.slice(0, -1));
        handleSend(lastUserMsg.content);
      }
    }
  };

  const handleMakeShorter = async () => {
    if (messages.length >= 1) {
      const lastAssistant = [...messages].reverse().find(m => m.type === 'assistant');
      if (lastAssistant?.content) {
        const data = lastAssistant.content;
        // Handle both response and coaching mode
        const messageToShorten = data.whatsapp_version || data.whatsapp_message || data.optional_message || data.suggested_reply;
        if (messageToShorten) {
          handleSend(`Make this message shorter and more concise: "${messageToShorten}"`);
        }
      }
    }
  };

  // iOS Safari Keyboard handling with VisualViewport API
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  
  useEffect(() => {
    // Prevent body scroll when SAIRA is active on mobile
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);
  
  useEffect(() => {
    // VisualViewport API for iOS Safari keyboard handling
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      // Calculate keyboard offset
      const offset = window.innerHeight - viewport.height - viewport.offsetTop;
      setKeyboardOffset(Math.max(0, offset));
      
      // Scroll to bottom when keyboard opens
      if (offset > 0 && messagesEndRef.current) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Render CLARIFICATION mode (SAIRA asking questions)
  const renderClarificationResponse = (data, msg, idx) => {
    return (
      <div className="space-y-3">
        {/* Clarification Message */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle size={16} className="text-purple-600" />
            <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Before I advise...</span>
          </div>
          <p className="text-sm text-slate-700 mb-4">{data.clarification_message}</p>
          
          {/* Clarification Questions */}
          {data.clarification_questions?.length > 0 && (
            <div className="space-y-2">
              {data.clarification_questions.map((question, i) => (
                <button
                  key={i}
                  onClick={() => handleSend(question)}
                  className="w-full text-left p-3 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors border border-purple-200"
                >
                  <p className="text-sm text-purple-800">{question}</p>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <p className="text-xs text-slate-400 text-center">
          Tap a question to answer, or type your own response
        </p>
      </div>
    );
  };

  // Render COACHING mode response (Manager style)
  const renderCoachingResponse = (data, msg, idx) => {
    // Check if this is a clarification response
    if (data.needs_clarification) {
      return renderClarificationResponse(data, msg, idx);
    }
    
    const isExpanded = expandedResponse === idx;
    
    // Check if this is a factual response (has situation_summary but no strategy)
    const isFactualResponse = data.situation_summary && 
      (!data.recommended_strategy || data.recommended_strategy.length === 0) &&
      !data.risk_opportunity;
    
    if (isFactualResponse) {
      // Render factual answer with follow-up offer
      return (
        <div className="space-y-3">
          {/* Factual Answer */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.situation_summary}</p>
          </div>
          
          {/* Follow-up Question (if present) */}
          {data.optional_message && data.optional_message !== 'null' && (
            <div className="bg-purple-50 rounded-2xl p-4 border border-purple-200">
              <p className="text-sm text-purple-800">{data.optional_message}</p>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleSend("Yes, this is for an active customer")}
                  className="h-8 text-xs bg-purple-600 hover:bg-purple-700"
                >
                  Active customer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSend("Just general prep for now")}
                  className="h-8 text-xs border-purple-200 text-purple-700 hover:bg-purple-100"
                >
                  General prep
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // Full strategic response
    return (
      <div className="space-y-3">
        {/* Situation Summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={14} className="text-purple-600" />
            <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Situation Summary</span>
          </div>
          <p className="text-sm text-slate-700">{data.situation_summary}</p>
        </div>
        
        {/* Risk / Opportunity */}
        {data.risk_opportunity && (
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={14} className="text-amber-600" />
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Risk / Opportunity</span>
            </div>
            <p className="text-sm text-amber-800">{data.risk_opportunity}</p>
          </div>
        )}
        
        {/* Recommended Strategy */}
        {data.recommended_strategy?.length > 0 && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Target size={14} className="text-blue-600" />
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Recommended Strategy</span>
            </div>
            <ul className="space-y-2">
              {data.recommended_strategy.map((strategy, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                  <span className="text-sm text-blue-800">{strategy}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Next Best Action - Highlighted */}
        {data.next_best_action && (
          <div className="bg-emerald-50 rounded-2xl p-4 border-2 border-emerald-300">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={14} className="text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Next Best Action (24-48 hrs)</span>
            </div>
            <p className="text-sm text-emerald-800 font-medium">{data.next_best_action}</p>
          </div>
        )}
        
        {/* Optional Message (only if provided and has strategy) */}
        {data.optional_message && data.optional_message !== 'null' && (
          <>
            <button
              onClick={() => setExpandedResponse(isExpanded ? null : idx)}
              className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {isExpanded ? 'Hide message suggestion' : 'Show optional message'}
            </button>
            
            {isExpanded && (
              <div className="bg-green-50 rounded-2xl p-4 border border-green-200 animate-in fade-in duration-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MessageCircle size={14} className="text-green-600" />
                    <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Optional Message</span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(data.optional_message, msg.conversationId)}
                      className="h-7 px-2 text-green-700 hover:bg-green-100"
                    >
                      <Copy size={12} className="mr-1" />
                      Copy
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleShareWhatsApp(data.optional_message, msg.conversationId)}
                      className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <ExternalLink size={12} className="mr-1" />
                      Share
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-green-800 whitespace-pre-wrap">{data.optional_message}</p>
              </div>
            )}
          </>
        )}
        
        {/* Quick actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            className="h-7 text-xs"
          >
            <RefreshCw size={12} className="mr-1" />
            Regenerate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSend("Draft a WhatsApp message based on your recommendation")}
            className="h-7 text-xs border-green-200 text-green-700 hover:bg-green-50"
          >
            <MessageCircle size={12} className="mr-1" />
            Draft Message
          </Button>
        </div>
      </div>
    );
  };

  // Render RESPONSE mode (Draft messages)
  const renderResponseMode = (data, msg, idx) => {
    const isExpanded = expandedResponse === idx;
    
    return (
      <div className="space-y-3">
        {/* Suggested Reply */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageCircle size={14} className="text-purple-600" />
              <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Suggested Reply</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopy(data.suggested_reply, msg.conversationId)}
              className="h-7 px-2"
            >
              <Copy size={12} className="mr-1" />
              Copy
            </Button>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.suggested_reply}</p>
        </div>
        
        {/* WhatsApp Version */}
        <div className="bg-green-50 rounded-2xl p-4 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-green-600">📱</span>
              <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">WhatsApp Version</span>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy(data.whatsapp_version, msg.conversationId)}
                className="h-7 px-2 text-green-700 hover:bg-green-100"
              >
                <Copy size={12} className="mr-1" />
                Copy
              </Button>
              <Button
                size="sm"
                onClick={() => handleShareWhatsApp(data.whatsapp_version, msg.conversationId)}
                className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
              >
                <ExternalLink size={12} className="mr-1" />
                Share
              </Button>
            </div>
          </div>
          <p className="text-sm text-green-800 whitespace-pre-wrap">{data.whatsapp_version}</p>
        </div>
        
        {/* Shorter Version (expandable) */}
        {data.shorter_version && (
          <>
            <button
              onClick={() => setExpandedResponse(isExpanded ? null : idx)}
              className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700"
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {isExpanded ? 'Hide shorter version' : 'Show shorter version'}
            </button>
            
            {isExpanded && (
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 animate-in fade-in duration-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Ultra-Short Version</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(data.shorter_version, msg.conversationId)}
                    className="h-7 px-2"
                  >
                    <Copy size={12} className="mr-1" />
                    Copy
                  </Button>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.shorter_version}</p>
              </div>
            )}
          </>
        )}
        
        {/* Quick actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            className="h-7 text-xs"
          >
            <RefreshCw size={12} className="mr-1" />
            Regenerate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMakeShorter}
            className="h-7 text-xs"
          >
            ✂️ Shorter
          </Button>
        </div>
      </div>
    );
  };

  // Render PRODUCT mode (Project Knowledge)
  const renderProductMode = (data, msg, idx) => {
    return (
      <div className="space-y-3">
        {/* Project Overview */}
        {data.overview && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🏗</span>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Project Overview</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.overview}</p>
          </div>
        )}
        
        {/* Ideal Buyer Profile */}
        {data.ideal_buyer_profile && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🎯</span>
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Ideal Buyer Profile</span>
            </div>
            <p className="text-sm text-blue-800">{data.ideal_buyer_profile}</p>
          </div>
        )}
        
        {/* Positioning Strategy */}
        {data.positioning_strategy && (
          <div className="bg-purple-50 rounded-2xl p-4 border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">💬</span>
              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Positioning Strategy</span>
            </div>
            <p className="text-sm text-purple-800">{data.positioning_strategy}</p>
          </div>
        )}
        
        {/* Competitive Angle */}
        {data.competitive_angle && (
          <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">⚔</span>
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Competitive Angle</span>
            </div>
            <p className="text-sm text-emerald-800">{data.competitive_angle}</p>
          </div>
        )}
        
        {/* Common Objections - Battle Cards */}
        {data.common_objections && (
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">⚠</span>
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Common Objections</span>
            </div>
            
            {/* Handle both array format and legacy string format */}
            {Array.isArray(data.common_objections) ? (
              <div className="space-y-3">
                {data.common_objections.map((item, i) => (
                  <ObjectionCard key={i} item={item} index={i} />
                ))}
              </div>
            ) : (
              // Legacy string format fallback
              <p className="text-sm text-amber-800 whitespace-pre-wrap">{data.common_objections}</p>
            )}
          </div>
        )}
        
        {/* Personalized Positioning (if lead context available) */}
        {data.personalized_positioning && (
          <div className="bg-green-50 rounded-2xl p-4 border-2 border-green-300">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🔄</span>
              <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">For This Customer</span>
            </div>
            <p className="text-sm text-green-800 font-medium">{data.personalized_positioning}</p>
          </div>
        )}
      </div>
    );
  };

  // Render PERFORMANCE mode (Analytics)
  const renderPerformanceMode = (data, msg, idx) => {
    return (
      <div className="space-y-3">
        {/* Performance Snapshot */}
        {data.snapshot && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📊</span>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Performance Snapshot</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.snapshot}</p>
          </div>
        )}
        
        {/* Pattern Insight */}
        {data.pattern_insight && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🔍</span>
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Pattern Insight</span>
            </div>
            <p className="text-sm text-blue-800">{data.pattern_insight}</p>
          </div>
        )}
        
        {/* Risk Area */}
        {data.risk_area && (
          <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">⚠</span>
              <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Risk Area</span>
            </div>
            <p className="text-sm text-red-800">{data.risk_area}</p>
          </div>
        )}
        
        {/* Acceleration Strategy */}
        {data.acceleration_strategy?.length > 0 && (
          <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🚀</span>
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Acceleration Strategy</span>
            </div>
            <ul className="space-y-2">
              {data.acceleration_strategy.map((strategy, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                  <span className="text-sm text-emerald-800">{strategy}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* 7-Day Action Plan */}
        {data.seven_day_plan?.length > 0 && (
          <div className="bg-purple-50 rounded-2xl p-4 border-2 border-purple-300">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🎯</span>
              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">7-Day Action Plan</span>
            </div>
            <ul className="space-y-2">
              {data.seven_day_plan.map((action, i) => (
                <li key={i} className="text-sm text-purple-800 bg-white rounded-lg p-2 border border-purple-200">
                  {action}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Render SYSTEM mode (Operations Query)
  const renderSystemMode = (data, msg, idx) => {
    return (
      <div className="space-y-3">
        {/* Data Summary */}
        {data.data_summary && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📋</span>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Summary</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.data_summary}</p>
          </div>
        )}
        
        {/* Current Status */}
        {data.current_status && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📍</span>
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Current Status</span>
            </div>
            <p className="text-sm text-blue-800">{data.current_status}</p>
          </div>
        )}
        
        {/* Pending Items */}
        {data.pending_items && (
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">⏳</span>
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Pending Items</span>
            </div>
            <p className="text-sm text-amber-800">{data.pending_items}</p>
          </div>
        )}
        
        {/* Next Step */}
        {data.next_step && (
          <div className="bg-emerald-50 rounded-2xl p-4 border-2 border-emerald-300">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">➡️</span>
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Next Step</span>
            </div>
            <p className="text-sm text-emerald-800 font-medium">{data.next_step}</p>
          </div>
        )}
        
        {/* Escalation Needed */}
        {data.escalation_needed && (
          <div className="bg-red-100 rounded-2xl p-4 border-2 border-red-300">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-600" />
              <span className="text-sm font-semibold text-red-700">Escalation may be required</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render CRM_COPILOT mode (SAIRA 2.0 - Rep CRM Assistant)
  const renderCrmCopilotMode = (data, msg, idx) => {
    return (
      <div className="space-y-3">
        {/* Main Answer */}
        {data.answer && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📋</span>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Answer</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.answer}</p>
          </div>
        )}
        
        {/* Customer List */}
        {data.customer_list?.length > 0 && (
          <div className="bg-purple-50 rounded-2xl p-4 border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">👥</span>
              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Customers</span>
            </div>
            <ul className="space-y-2">
              {data.customer_list.map((customer, i) => (
                <li key={i} className="text-sm text-purple-800 bg-white rounded-lg p-2 border border-purple-200 flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span>{customer}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Count Summary */}
        {data.count_summary && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📊</span>
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Pipeline Summary</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.count_summary).map(([key, value]) => (
                <Badge key={key} variant="secondary" className="bg-white text-blue-700 border border-blue-200">
                  {key.replace(/_/g, ' ')}: {value}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Priority Recommendation */}
        {data.priority_recommendation && (
          <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-300">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🎯</span>
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Priority Focus</span>
            </div>
            <p className="text-sm text-amber-800 whitespace-pre-wrap">{data.priority_recommendation}</p>
          </div>
        )}
        
        {/* Action Items */}
        {data.action_items?.length > 0 && (
          <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">✅</span>
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Action Items</span>
            </div>
            <ul className="space-y-2">
              {data.action_items.map((action, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                  <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Render MARKET_INTEL mode (SAIRA 2.0 - Market Intelligence)
  const renderMarketIntelMode = (data, msg, idx) => {
    return (
      <div className="space-y-3">
        {/* News Summary */}
        {data.summary && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📰</span>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Market Update</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.summary}</p>
          </div>
        )}
        
        {/* Location Context */}
        {data.location_context && (
          <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📍</span>
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Location Impact</span>
            </div>
            <p className="text-sm text-blue-800">{data.location_context}</p>
          </div>
        )}
        
        {/* Sales Impact */}
        {data.sales_impact && (
          <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">💰</span>
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Sales Impact</span>
            </div>
            <p className="text-sm text-emerald-800">{data.sales_impact}</p>
          </div>
        )}
        
        {/* Talking Points */}
        {data.talking_points?.length > 0 && (
          <div className="bg-purple-50 rounded-2xl p-4 border-2 border-purple-300">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">💬</span>
              <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Customer Talking Points</span>
            </div>
            <ul className="space-y-2">
              {data.talking_points.map((point, i) => (
                <li key={i} className="text-sm text-purple-800 bg-white rounded-lg p-3 border border-purple-200">
                  {point}
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(point.replace(/^💬\s*/, ''));
                      toast.success('Copied to clipboard!');
                    }}
                    className="mt-2 flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                  >
                    <Copy size={12} />
                    Copy
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Urgency Trigger */}
        {data.urgency_trigger && (
          <div className="bg-amber-50 rounded-2xl p-4 border-2 border-amber-300">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">⏰</span>
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Urgency Factor</span>
            </div>
            <p className="text-sm text-amber-800 font-medium">{data.urgency_trigger}</p>
          </div>
        )}
        
        {/* Related News */}
        {data.related_news?.length > 0 && (
          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📌</span>
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Related Updates</span>
            </div>
            <ul className="space-y-1">
              {data.related_news.map((news, i) => (
                <li key={i} className="text-sm text-slate-600">
                  {news}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Main render function that routes to correct mode
  const renderAssistantMessage = (msg, idx) => {
    let data = msg.content;
    
    // Handle case where content is a string (corrupted or legacy data)
    if (typeof data === 'string') {
      try {
        // Try to extract JSON from the string
        const jsonMatch = data.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          data = JSON.parse(jsonMatch[0]);
        } else {
          // Fallback: show as plain text
          return (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{data}</p>
            </div>
          );
        }
      } catch (e) {
        // If JSON parsing fails, display as plain text
        return (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data}</p>
          </div>
        );
      }
    }
    
    const mode = data?.mode || 'coaching';
    
    // Handle legacy responses (before multi-mode update)
    if (!data?.mode && (data?.suggested_reply || data?.whatsapp_message)) {
      return renderResponseMode({
        ...data,
        mode: 'response',
        whatsapp_version: data.whatsapp_message || data.whatsapp_version,
        shorter_version: data.shorter_version || null
      }, msg, idx);
    }
    
    // Route to correct renderer based on mode
    switch (mode) {
      case 'response':
        return renderResponseMode(data, msg, idx);
      case 'product':
        return renderProductMode(data, msg, idx);
      case 'performance':
        return renderPerformanceMode(data, msg, idx);
      case 'system':
        return renderSystemMode(data, msg, idx);
      case 'crm_copilot':
        return renderCrmCopilotMode(data, msg, idx);
      case 'market_intel':
        return renderMarketIntelMode(data, msg, idx);
      case 'coaching':
      default:
        return renderCoachingResponse(data, msg, idx);
    }
  };

  return (
    <div className="h-[100dvh] bg-gradient-to-b from-purple-50 to-white flex flex-col overflow-hidden">
      {/* Fixed Header - Never moves */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Home Button - Primary navigation */}
            <button 
              onClick={() => navigate('/')} 
              className="p-2 -ml-1 hover:bg-slate-100 rounded-lg"
              data-testid="home-btn"
            >
              <Home size={20} className="text-slate-600" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-purple-600" />
                <h1 className="font-bold text-lg text-slate-900">SAIRA</h1>
              </div>
              <p className="text-xs text-slate-500">Your 24/7 Sales Coach</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* About SAIRA Info Icon - only when messages exist */}
            {messages.length > 0 && (
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    className="p-2 hover:bg-purple-50 rounded-lg text-slate-400 hover:text-purple-600"
                    title="About SAIRA"
                    data-testid="saira-about-btn"
                  >
                    <Info size={16} />
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-2xl">
                  <SheetHeader className="text-left">
                    <SheetTitle className="flex items-center gap-2">
                      <Sparkles size={20} className="text-purple-600" />
                      Hi, I'm SAIRA 👋
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-4 space-y-4">
                    <p className="text-sm text-slate-600 font-medium">
                      Sales AI Realestate Advisor — your 24/7 Sales Coach
                    </p>
                    <ul className="space-y-2">
                      {SAIRA_CAPABILITIES.map((capability, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="text-purple-500 mt-0.5">•</span>
                          <span>{capability}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="bg-purple-50 rounded-xl p-3">
                      <p className="text-xs text-purple-700">
                        <span className="font-semibold">💡 Tip:</span> Attach lead context for better answers.
                      </p>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}
            {messages.length > 0 && (
              <button
                onClick={() => {
                  setMessages([]);
                  localStorage.removeItem('saira_messages');
                  toast.success('Chat cleared');
                }}
                className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500"
                title="Clear chat"
              >
                <RefreshCw size={16} />
              </button>
            )}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <ChevronDown size={18} className={`text-slate-600 transition-transform ${showSettings ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Settings Panel */}
        {showSettings && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-3 animate-in fade-in duration-200">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="concise">Concise</SelectItem>
                    <SelectItem value="premium">Premium Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Channel</label>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              💡 SAIRA auto-detects language — ask in Hindi/Hinglish, get response in Hinglish!
            </p>
          </div>
        )}
        
        {/* Lead Context Badge */}
        {selectedLead && (
          <div className="mt-3 flex items-center gap-2">
            <Badge className="bg-purple-100 text-purple-700 text-xs">
              <User size={10} className="mr-1" />
              {selectedLead.name}
            </Badge>
            {selectedLead.project && (
              <Badge variant="outline" className="text-xs">
                {selectedLead.project}
              </Badge>
            )}
            <button
              onClick={() => setSelectedLead(null)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              ✕ Clear
            </button>
          </div>
        )}
        
        {/* Attach Lead Button */}
        {!selectedLead && (
          <button
            onClick={() => setShowLeadPicker(true)}
            className="mt-3 text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            <User size={12} />
            Attach lead context (optional)
          </button>
        )}
      </div>
      
      {/* Scrollable Messages Area - Takes remaining space */}
      <div 
        ref={contentRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        style={{ 
          paddingBottom: keyboardOffset > 0 ? '16px' : '16px',
          minHeight: 0 // Important for flex child to scroll
        }}
      >
        {messages.length === 0 && (
          <div className="py-6" data-testid="saira-empty-state">
            {/* Header with SAIRA Introduction */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 mb-6 border border-purple-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                  <Sparkles size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-slate-800 text-lg flex items-center gap-1">
                    Hi, I'm SAIRA 👋
                  </h2>
                  <p className="text-sm text-purple-600 font-medium">
                    Sales AI Realestate Advisor — your 24/7 Sales Coach
                  </p>
                </div>
              </div>
              
              {/* Capabilities List */}
              <ul className="space-y-2 mt-4">
                {SAIRA_CAPABILITIES.map((capability, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-purple-500 mt-0.5">•</span>
                    <span>{capability}</span>
                  </li>
                ))}
              </ul>
              
              {/* Tip */}
              <div className="mt-4 bg-white/60 rounded-xl p-3 border border-purple-100">
                <p className="text-xs text-purple-700">
                  <span className="font-semibold">💡 Tip:</span> Attach lead context for better answers.
                </p>
              </div>
            </div>
            
            {/* Quick Suggestion Chips */}
            <div className="space-y-3">
              <p className="text-xs text-slate-500 text-center font-medium">Try asking...</p>
              <div className="flex flex-col gap-2">
                {EMPTY_STATE_CHIPS.map((chip, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInputValue(chip.label)}
                    data-testid={`saira-quick-chip-${idx}`}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-purple-50 hover:border-purple-300 transition-all flex items-center gap-3 text-left"
                  >
                    <span className="text-lg">{chip.icon}</span>
                    <span>{chip.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[90%] ${msg.type === 'user' ? 'bg-purple-600 text-white rounded-2xl rounded-br-md px-4 py-2' : ''}`}>
              {msg.type === 'user' ? (
                <div>
                  <p className="text-sm">{msg.content}</p>
                  <p className="text-xs text-purple-200 mt-1 text-right">{formatTime(msg.timestamp)}</p>
                </div>
              ) : (
                renderAssistantMessage(msg, idx)
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-slate-100 max-w-[85%]">
              {loadingState === 'thinking' && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm">SAIRA is thinking...</span>
                </div>
              )}
              
              {loadingState === 'slow' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-amber-600">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-sm font-medium">Taking longer than usual... ({loadingDuration}s)</span>
                  </div>
                  <p className="text-xs text-slate-500">Complex queries take more time. Please wait.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelRequest}
                    className="h-7 text-xs text-slate-500"
                  >
                    Cancel request
                  </Button>
                </div>
              )}
              
              {loadingState === 'timeout' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-500">
                    <AlertTriangle size={16} />
                    <span className="text-sm font-medium">Request is taking very long ({loadingDuration}s)</span>
                  </div>
                  <p className="text-xs text-slate-500">The AI might be overloaded. You can wait or cancel.</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelRequest}
                      className="h-7 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {loadingState === 'error' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-500">
                    <AlertTriangle size={16} />
                    <span className="text-sm font-medium">Something went wrong</span>
                  </div>
                  <p className="text-xs text-slate-500">SAIRA couldn't respond. This might be a temporary issue.</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleRetry}
                      className="h-7 text-xs bg-purple-600 hover:bg-purple-700"
                    >
                      <RefreshCw size={12} className="mr-1" />
                      Retry
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Fixed Bottom Section - Quick Chips + Input */}
      <div 
        className="flex-shrink-0 bg-white border-t border-slate-200 z-20"
        style={{ 
          transform: `translateY(-${keyboardOffset}px)`,
          transition: 'transform 0.1s ease-out'
        }}
      >
        {/* Quick Chips (show when there are messages) */}
        {messages.length > 0 && (
          <div className="px-4 py-2 overflow-x-auto flex gap-2 border-b border-slate-100">
            {QUICK_CHIPS.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => handleSend(chip.prompt)}
                disabled={isLoading}
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-medium text-slate-600 hover:bg-purple-50 hover:border-purple-200 transition-colors whitespace-nowrap flex items-center gap-1"
              >
                <span>{chip.icon}</span>
                {chip.label}
              </button>
            ))}
          </div>
        )}
        
        {/* Input Area - Keyboard safe */}
        <div className="px-4 py-3 safe-bottom">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Ask SAIRA for sales guidance..."
              className="flex-1 rounded-full"
              disabled={isLoading}
            />
            <Button
              onClick={() => handleSend()}
              disabled={isLoading || !inputValue.trim()}
              className="rounded-full bg-purple-600 hover:bg-purple-700 px-4"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Lead Picker Dialog */}
      <Dialog open={showLeadPicker} onOpenChange={setShowLeadPicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User size={18} className="text-purple-600" />
              Select a Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchLeads(e.target.value);
                }}
                placeholder="Search by name or email..."
                className="pl-9"
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto space-y-2">
              {searchLoading && (
                <div className="text-center py-4">
                  <Loader2 size={20} className="animate-spin mx-auto text-slate-400" />
                </div>
              )}
              
              {!searchLoading && searchResults.map((lead) => (
                <button
                  key={lead.customer_id}
                  onClick={() => {
                    setSelectedLead({
                      id: lead.customer_id,
                      name: lead.customer_name,
                      project: lead.first_project_name,
                      phone: lead.customer_phone
                    });
                    setShowLeadPicker(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="w-full text-left p-3 bg-slate-50 hover:bg-purple-50 rounded-xl transition-colors"
                >
                  <p className="font-medium text-sm text-slate-800">{lead.customer_name}</p>
                  <p className="text-xs text-slate-500">{lead.customer_email}</p>
                  {lead.first_project_name && (
                    <Badge variant="outline" className="mt-1 text-xs">{lead.first_project_name}</Badge>
                  )}
                </button>
              ))}
              
              {!searchLoading && searchQuery && searchResults.length === 0 && (
                <p className="text-center text-sm text-slate-500 py-4">No leads found</p>
              )}
            </div>
            
            <Button
              variant="outline"
              onClick={() => {
                setShowLeadPicker(false);
                setSearchQuery('');
              }}
              className="w-full"
            >
              Continue without lead context
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SAIRA;
